import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, Upload, Wand2, UserCog, ArrowRight, Rocket, Camera, BookOpen } from "lucide-react";
import { Card, PageHeader, Stat, StatusPill, Empty } from "../../components/dash/Kit";
import UsageMeter, { useUsage } from "../../components/dash/UsageMeter";
import TrialBanner from "../../components/dash/TrialBanner";
import { CONTENT_STATUSES } from "../../data/site";
import { api } from "../../lib/api";

const quick = [
  ["Create Post", "/dashboard/create", Wand2],
  ["Promote Something", "/dashboard/campaigns", Rocket],
  ["Upload Photo", "/dashboard/media", Upload],
  ["Photo To Post", "/dashboard/photo", Camera],
  ["How To Post", "/dashboard/how-to-post", BookOpen],
  ["Add Promotion", "/dashboard/promotions", Megaphone],
  ["Edit My Brand", "/dashboard/brand", UserCog],
];

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

export default function Overview() {
  const [data, setData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [insights, setInsights] = useState(null);
  const { usage } = useUsage();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData(false));
    api.get("/notifications").then((r) => setNotifications(r.data)).catch(() => {});
    api.get("/engine/drafts").then((r) => setDrafts(r.data)).catch(() => {});
    api.get("/insights/weekly").then((r) => setInsights(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return <Card testId="overview-loading">Loading your dashboard…</Card>;
  }

  const stepIndex = CONTENT_STATUSES.indexOf(data.content_status);
  const awaiting = drafts.filter((d) => d.status === "AI Draft" || d.status === "Needs Review");

  return (
    <div className="space-y-6" data-testid="dashboard-overview">
      <PageHeader
        testId="overview-header"
        title={`${greeting()}, ${data.user.first_name}.`}
        body="Here's where your social media stands this month."
      />

      <TrialBanner trial={data.trial} plan={data.package} />

      {!data.profile_completed && (
        <Card testId="overview-profile-alert" className="border-amber-200 bg-amber-50">
          <p className="font-display text-lg font-semibold text-amber-900">Let's put your social media on autopilot</p>
          <p className="mt-2 text-sm text-amber-900/80">
            Run the quick setup and PostMint builds your Brand Profile for you — most of it is filled in from your website.
          </p>
          <Link
            to="/dashboard/setup"
            data-testid="overview-complete-profile-link"
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white"
          >
            Start quick setup <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat testId="stat-package" label="Your Plan" value={data.package.name} hint={data.trial?.on_trial && !data.trial?.expired ? `${data.trial.days_left} days left, €0 charged so far` : data.package.price === 0 ? "Choose a plan to continue" : `€${data.package.price}/month`} />
        <Stat testId="stat-waiting-approval" label="Waiting For Approval" value={awaiting.length} />
        <Stat testId="stat-posts" label="Posts This Month" value={data.posts_count} />
        <Stat testId="stat-active-campaigns" label="Drafts Created" value={drafts.length} />
      </div>

      <UsageMeter usage={usage} testId="overview-usage-meter" />

      {insights && (
        <Card testId="overview-insights-card" className="border-[#F95738]">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#B93615]">PostMint Insights</p>
              <p className="mt-2 font-display text-lg font-semibold text-slate-900">{insights.headline}</p>
              <ul className="mt-3 space-y-1.5">
                {(insights.insights || []).slice(0, 3).map((i) => (
                  <li key={i.text} className="text-sm text-slate-600">
                    · {i.text}
                  </li>
                ))}
              </ul>
            </div>
            <Link
              to="/dashboard/insights"
              data-testid="overview-insights-link"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
            >
              See my week <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </Card>
      )}

      <div>
        <p className="mb-3 font-display text-lg font-semibold text-slate-900">What would you like to do?</p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {quick.map(([label, to, Icon]) => (
            <Link
              key={to}
              to={to}
              data-testid={`quick-action-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="rounded-2xl border border-slate-200 bg-white p-4 transition-transform hover:-translate-y-0.5 soft-shadow"
            >
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#FFF3EF] text-[#F95738]">
                <Icon className="h-4 w-4" aria-hidden="true" />
              </span>
              <p className="mt-3 text-sm font-semibold text-slate-900">{label}</p>
            </Link>
          ))}
        </div>
      </div>

      {awaiting.length > 0 && (
        <div>
          <p className="mb-3 font-display text-lg font-semibold text-slate-900">Needs your approval</p>
          <div className="space-y-3" data-testid="overview-needs-approval">
            {awaiting.slice(0, 4).map((d) => (
              <Card key={d.id} testId={`overview-draft-${d.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-slate-900">
                    {d.platform} · {d.title || d.content_type}
                  </p>
                  <StatusPill status={d.status} />
                </div>
                <p className="mt-2 line-clamp-2 text-sm text-slate-600">{d.caption}</p>
              </Card>
            ))}
            <Link
              to="/dashboard/content"
              data-testid="overview-review-all-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#D8431F]"
            >
              Review all content <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      )}

      <Card testId="overview-status-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="font-display text-lg font-semibold text-slate-900">Content Status</p>
          <StatusPill status={data.content_status} testId="overview-content-status" />
        </div>
        <ol className="mt-5 grid gap-2 sm:grid-cols-4 lg:grid-cols-7">
          {CONTENT_STATUSES.map((s, i) => (
            <li key={s} className="min-w-0">
              <div
                className={`h-1.5 rounded-full ${i <= stepIndex ? "bg-[#F95738]" : "bg-slate-200"}`}
                aria-hidden="true"
              />
              <p className={`mt-2 text-xs ${i <= stepIndex ? "font-semibold text-slate-800" : "text-slate-400"}`}>{s}</p>
            </li>
          ))}
        </ol>
      </Card>

      <div>
        <p className="mb-3 font-display text-lg font-semibold text-slate-900">Recent updates</p>
        {notifications.length === 0 ? (
          <Empty testId="overview-no-notifications" title="No updates yet" body="Account updates will appear here." />
        ) : (
          <div className="space-y-3" data-testid="overview-notifications">
            {notifications.slice(0, 5).map((n) => (
              <Card key={n.id} testId={`notification-${n.id}`}>
                <p className="text-sm font-semibold text-slate-900">{n.title}</p>
                <p className="mt-1 text-sm text-slate-600">{n.body}</p>
                <p className="mt-2 text-xs text-slate-400">{new Date(n.created_at).toLocaleString()}</p>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


